import React, { useState } from 'react';
import { DEFAULT_MENU_PERMISSIONS } from '../utils/menuPermissions';
import './MenuManagement.css';

const MenuManagement = () => {
  // 메뉴 설정은 DEFAULT_MENU_PERMISSIONS(코드)에서만 읽음 - localStorage 미사용
  const [menuSettings, setMenuSettings] = useState(() => ({ ...DEFAULT_MENU_PERMISSIONS }));

  // 사용자 등급 정의
  const userGrades = [
    {
      id: 'guest',
      name: '게스트',
      level: 'Guest',
      icon: '👤',
      description: '비로그인 사용자'
    },
    {
      id: 'bronze',
      name: '브론즈',
      level: 'Level 1',
      icon: '🥉',
      description: '기본 사용자'
    },
    {
      id: 'silver',
      name: '실버',
      level: 'Level 2',
      icon: '🥈',
      description: '일반 사용자'
    },
    {
      id: 'gold',
      name: '골드',
      level: 'Level 3',
      icon: '🥇',
      description: '우수 사용자'
    },
    {
      id: 'platinum',
      name: '플래티넘',
      level: 'Level 4',
      icon: '💎',
      description: '프리미엄 사용자'
    },
    {
      id: 'diamond',
      name: '다이아몬드',
      level: 'Level 5',
      icon: '💠',
      description: '최고급 사용자'
    }
  ];

  // 사용 가능한 메뉴 목록
  const availableMenus = [
    {
      id: 'trading',
      name: '거래대금상위',
      icon: '📈',
      description: '실시간 거래대금 상위 종목 조회',
      path: '/realtime',
      category: 'main'
    },
    {
      id: 'algorithm',
      name: '알고리즘',
      icon: '🤖',
      description: 'AI 알고리즘 기반 종목 추천 시스템',
      path: '/algorithm-pressed',
      category: 'main'
    },
    {
      id: 'stock-info',
      name: '주식 정보',
      icon: '📊',
      description: '증권리포트 등 주식 정보 조회',
      path: '/stock-report',
      category: 'main'
    },
    {
      id: 'board',
      name: '게시판',
      icon: '📋',
      description: '커뮤니티 게시판 시스템',
      path: '/board',
      category: 'main'
    },
    {
      id: 'news-alert',
      name: '뉴스알림',
      icon: '📰',
      description: '키워드 기반 뉴스 알림 서비스',
      path: '/news-alert',
      category: 'premium'
    },
    {
      id: 'study',
      name: '스터디',
      icon: '📝',
      description: '주식 종목/섹터 분석 스터디 기록 서비스',
      path: '/studies',
      category: 'main'
    },
    {
      id: 'crawling',
      name: '크롤링',
      icon: '🕷️',
      description: '웹 크롤링 요청 관리 시스템',
      path: '/crawling',
      category: 'admin'
    },
    {
      id: 'admin',
      name: '관리자 페이지',
      icon: '⚙️',
      description: '시스템 관리 및 설정',
      path: '/admin',
      category: 'admin'
    }
  ];



  const toggleMenu = (gradeId, menuId) => {
    setMenuSettings(prev => {
      const gradeMenus = prev[gradeId] || [];
      const newGradeMenus = gradeMenus.includes(menuId)
        ? gradeMenus.filter(id => id !== menuId)
        : [...gradeMenus, menuId];

      return {
        ...prev,
        [gradeId]: newGradeMenus
      };
    });
  };

  const enableAllMenus = (gradeId) => {
    setMenuSettings(prev => ({
      ...prev,
      [gradeId]: availableMenus.map(menu => menu.id)
    }));
  };

  const disableAllMenus = (gradeId) => {
    setMenuSettings(prev => ({
      ...prev,
      [gradeId]: []
    }));
  };

  const resetToDefault = (gradeId) => {
    setMenuSettings(prev => ({
      ...prev,
      [gradeId]: DEFAULT_MENU_PERMISSIONS[gradeId] || []
    }));
  };

  const getEnabledMenuCount = (gradeId) => {
    return (menuSettings[gradeId] || []).length;
  };

  const isMenuEnabled = (gradeId, menuId) => {
    return (menuSettings[gradeId] || []).includes(menuId);
  };

  return (
    <div className="menu-management">
      {/* Header */}
      <div className="menu-header">
        <div>
          <h2 className="menu-title">메뉴 권한 관리</h2>
          <p className="menu-subtitle">등급별 메뉴 권한 현황입니다. 변경은 <code>menuPermissions.js</code>의 <code>DEFAULT_MENU_PERMISSIONS</code>를 수정하세요.</p>
        </div>
      </div>

      {/* 사용 안내 */}
      <div className="info-box">
        <div className="info-header">
          <span className="info-icon">💡</span>
          <strong>메뉴 권한 변경 방법</strong>
        </div>
        <ol className="info-steps">
          <li><code>src/utils/menuPermissions.js</code>의 <code>DEFAULT_MENU_PERMISSIONS</code> 수정</li>
          <li><code>src/pages/MenuManagement.js</code>의 <code>defaultMenuSettings</code> 동일하게 수정</li>
          <li>저장 후 자동 반영 (새로고침)</li>
        </ol>
      </div>

      {/* Grade Sections */}
      <div className="grade-sections">
        {userGrades.map(grade => (
          <div key={grade.id} className="grade-section">
            <div className={`grade-header ${grade.id}`}>
              <div className="grade-info">
                <span className="grade-icon">{grade.icon}</span>
                <div>
                  <div className="grade-name">{grade.name}</div>
                  <div className="grade-level">{grade.level} - {grade.description}</div>
                </div>
              </div>
              <div className="menu-count">
                {getEnabledMenuCount(grade.id)}개 메뉴 활성화
              </div>
            </div>

            <div className="menu-content">
              {/* Permission Panel */}
              <div className="permission-panel">
                <div className="permission-title">
                  <span>🔧</span>
                  일괄 설정
                </div>
                <div className="permission-actions">
                  <button
                    onClick={() => enableAllMenus(grade.id)}
                    className="permission-btn enable-all-btn"
                  >
                    전체 활성화
                  </button>
                  <button
                    onClick={() => disableAllMenus(grade.id)}
                    className="permission-btn disable-all-btn"
                  >
                    전체 비활성화
                  </button>
                  <button
                    onClick={() => resetToDefault(grade.id)}
                    className="permission-btn reset-btn"
                  >
                    기본값으로 복원
                  </button>
                </div>
              </div>

              {/* Menu List */}
              <div className="menu-list">
                {availableMenus.map(menu => (
                  <div
                    key={menu.id}
                    className={`menu-item ${isMenuEnabled(grade.id, menu.id) ? 'enabled' : ''}`}
                    onClick={() => toggleMenu(grade.id, menu.id)}
                  >
                    <div className="menu-item-header">
                      <div className="menu-item-info">
                        <span className="menu-item-icon">{menu.icon}</span>
                        <span className="menu-item-name">{menu.name}</span>
                      </div>
                      <button
                        className={`menu-toggle ${isMenuEnabled(grade.id, menu.id) ? 'enabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(grade.id, menu.id);
                        }}
                      >
                        <div className="menu-toggle-slider"></div>
                      </button>
                    </div>
                    <div className="menu-item-description">
                      {menu.description}
                    </div>
                    <div className="menu-item-path">
                      {menu.path}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MenuManagement;